export const DIFF_NO_HIGHLIGHT = 0;
export const DIFF_HIGHLIGHT = 1;

export type HighlightPart = {
  text: string;
  kind: number;
};

export type LineHighlights = {
  base: HighlightPart[];
  head: HighlightPart[];
};

/**
 * Given a base and head text, extracts parts of each that should be
 * highlighted.  We currently use a simple approach that looks for a common
 * prefix and a common suffix.  Anything in between is highlighted.  Multiline
 * strings are not highlighted.  This is based on git's diff-highlight tool.
 * https://github.com/git/git/tree/master/contrib/diff-highlight
 *
 * This runs in O(n + m) where n and m are the length of the base and head
 * strings.
 */
export function extractLineHighlights(base: string | null | undefined, head: string | null | undefined): LineHighlights {
  // Check for non-existence.  Do not include the empty string here.  This
  // handles the added, removed, and both removed cases.
  //
  // Note: This treats undefined and null as equivalent for the purposes of
  // diffing.
  if (base === undefined || base === null ||
    head === undefined || head === null) {
    const highlights: LineHighlights = {base: [], head: []};
    // Non-existent things aren't displayed.
    if (base !== undefined && base !== null) {
      highlights.base.push({text: base, kind: DIFF_NO_HIGHLIGHT});
    }
    if (head !== undefined && head !== null) {
      highlights.head.push({text: head, kind: DIFF_NO_HIGHLIGHT});
    }
    return highlights;
  }

  // If either are multiline, give up.
  if (base.includes('\n') || head.includes('\n')) {
    return noHighlight(base, head);
  }

  const prefix = stringPrefix(base, head);

  // Handle the case when they're the same.  You probably shouldn't be calling
  // this when they're the same, but we need to handle it.
  if (prefix.length === base.length && base.length === head.length) {
    return noHighlight(base, head);
  }

  const suffix = stringSuffix(base, head, prefix.length);

  if (prefix.length + suffix.length > base.length ||
    prefix.length + suffix.length > head.length) {
    throw new Error(`prefix and suffix should be no longer than the full string: prefix=${prefix}, suffix=${suffix}, base=${base}, head=${head}`);
  }

  const baseParts: HighlightPart[] = [];
  const headParts: HighlightPart[] = [];
  // Prefix isn't highlighted.
  if (prefix.length > 0) {
    baseParts.push({text: prefix, kind: DIFF_NO_HIGHLIGHT});
    headParts.push({text: prefix, kind: DIFF_NO_HIGHLIGHT});
  }
  // Highlight everything in the middle.
  const baseMiddle = stringMiddle(base, prefix.length, suffix.length);
  if (baseMiddle.length > 0) {
    baseParts.push({text: baseMiddle, kind: DIFF_HIGHLIGHT});
  }
  const headMiddle = stringMiddle(head, prefix.length, suffix.length);
  if (headMiddle.length > 0) {
    headParts.push({text: headMiddle, kind: DIFF_HIGHLIGHT});
  }
  // Suffix isn't highlighted.
  if (suffix.length > 0) {
    baseParts.push({text: suffix, kind: DIFF_NO_HIGHLIGHT});
    headParts.push({text: suffix, kind: DIFF_NO_HIGHLIGHT});
  }

  return {base: baseParts, head: headParts};
}

function noHighlight(base: string, head: string): LineHighlights {
  return {
    base: [{text: base, kind: DIFF_NO_HIGHLIGHT}],
    head: [{text: head, kind: DIFF_NO_HIGHLIGHT}],
  };
}

function stringPrefix(s1: string, s2: string): string {
  const len1 = s1.length;
  const len2 = s2.length;
  let i = 0;
  while (i < len1 && i < len2 && s1[i] === s2[i]) {
    i++;
  }

  return s1.slice(0, i);
}

function stringSuffix(s1: string, s2: string, prefixLength: number): string {
  const len1 = s1.length;
  const len2 = s2.length;
  let i = 0;
  while (i < len1 && i < len2 && s1[len1 - 1 - i] === s2[len2 - 1 - i]) {
    // This extra condition is to prevent finding a common suffix that overlaps
    // with the common prefix.
    if (len1 - i <= prefixLength || len2 - i <= prefixLength) break;

    i++;
  }

  return s1.slice(len1 - i);
}

function stringMiddle(s: string, prefixLength: number, suffixLength: number): string {
  const suffixStart = s.length - suffixLength;

  return s.slice(prefixLength, suffixStart);
}
